import React, { useState, useEffect, useMemo } from "react";
import styles from "../../styles/styles";
import { State } from "country-state-city";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { server, backend_url } from "../../server";
import { toast } from "react-toastify";
import { addTocart, removeBundleDealFromCart, removeFromCart } from "../../redux/actions/cart";
import { getAllOrdersOfUser } from "../../redux/actions/order";
import { updatUserAddress } from "../../redux/actions/user";
import { fetchSiteOptions } from "../../redux/actions/siteOptions";
import { HiOutlineMinus, HiPlus } from "react-icons/hi";
import { RxCross1 } from "react-icons/rx";
import BundleRemovalWarningModal from "../Layout/BundleRemovalWarningModal";
import {
  sumCartSubtotal,
  sumPostageFees,
  computeBuyerProtectionFee,
  bundlePostageDiscountLabel,
  normalizeProductDisplayName,
} from "../../utils/checkoutPricing";
import {
  computeCartBundleTotals,
  defaultBundlePostageConfig,
} from "../../utils/bundlePricing";
import useEnrichedCartPostage from "../../hooks/useEnrichedCartPostage";
import { assertCartLinesInStock } from "../../utils/productStock";
import { cartRequiresUkara } from "../../utils/ukaraRequirement";

// Currency formatter for GBP
const formatCurrency = (value) => {
    if (value === null || value === undefined) return "£0.00";
    return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
    }).format(Number(value));
};

const Checkout = () => {
    const { user } = useSelector((state) => state.user);
    const { cart } = useSelector((state) => state.cart);
    const siteOptions = useSelector((state) => state.siteOptions?.options?.global);
    const dispatch = useDispatch();
    const [country] = useState("GB"); // UK-only, hidden from UI
    const [city, setCity] = useState("");
    const [userInfo, setUserInfo] = useState(true); // show saved addresses by default when available
    const [address1, setAddress1] = useState("");
    const [address2, setAddress2] = useState("");
    const [address3, setAddress3] = useState("");
    const [postCode, setPostCode] = useState(null);
    const [ukaraNumber, setUkaraNumber] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const navigate = useNavigate();
    const [bundleDiscount, setBundleDiscount] = useState(0);
    const [bundlePostageDiscount, setBundlePostageDiscount] = useState(0);
    const [bundlePostageConfig, setBundlePostageConfig] = useState(defaultBundlePostageConfig);
    const [discountCode, setDiscountCode] = useState("");
    const [appliedCode, setAppliedCode] = useState("");
    const [codeDiscount, setCodeDiscount] = useState(0);
    const [codeFreeShipping, setCodeFreeShipping] = useState(false);
    const [codeDiscountError, setCodeDiscountError] = useState("");
    const [codeDiscountLoading, setCodeDiscountLoading] = useState(false);
    const [codeStackable, setCodeStackable] = useState(false);
    const [buyerProtectionConfig, setBuyerProtectionConfig] = useState({
        enabled: true,
        fixedFee: 0.7,
        percentage: 2,
    });
    const { cartWithPostage } = useEnrichedCartPostage(cart);

    // Load site options (for veteran discount setting) if not already loaded
    useEffect(() => {
        if (!siteOptions && dispatch) {
            dispatch(fetchSiteOptions("global"));
        }
    }, [dispatch, siteOptions]);

    // Initialize user data from Redux - prioritize user data from registration/profile
    useEffect(() => {
        window.scrollTo(0, 0);
        if (user) {
            // Always use user data first (from registration/profile)
            setName(user.name || "");
            setEmail(user.email || "");
            setPhoneNumber(user.phoneNumber || "");
            setUkaraNumber(user.ukaraNumber || "");
            
            // Pre-fill address fields from user data (registration/profile)
            // Try saved addresses first (most recent/default)
            if (user.addresses && user.addresses.length > 0) {
                // Use the first saved address (or could use default/home type)
                const savedAddr = user.addresses.find(addr => addr.addressType === "Default") || user.addresses[0];
                if (savedAddr) {
                    if (savedAddr.address1) setAddress1(savedAddr.address1);
                    if (savedAddr.address2) setAddress2(savedAddr.address2);
                    if (savedAddr.address3) setAddress3(savedAddr.address3);
                    if (savedAddr.postCode) setPostCode(savedAddr.postCode);
                    if (savedAddr.city) setCity(savedAddr.city);
                }
            }
            // Fallback to shop address/post code from registration if no saved addresses
            else if (user.shopAddress || user.shopPostCode) {
                // shopAddress might be a single line, so we can use it as address1
                if (user.shopAddress && !address1) setAddress1(user.shopAddress);
                if (user.shopPostCode && !postCode) setPostCode(user.shopPostCode);
            }
        }
        
        // If user has previous orders, prefill address from the most recent order
        if (user?._id) {
            dispatch(getAllOrdersOfUser(user._id));
        }
    }, [user, dispatch]);

    // Prefill from previous orders - use as fallback if user data is missing
    useEffect(() => {
        if (user?._id) {
            axios.get(`${server}/order/get-all-orders/${user._id}`, { withCredentials: true })
                .then((res) => {
                    const orders = res.data?.orders || [];
                    if (orders.length > 0) {
                        // Get the most recent order
                        const latestOrder = orders[0];
                        
                        // Prefill address fields from previous order if not already set from user data
                        if (latestOrder?.shippingAddress) {
                            const addr = latestOrder.shippingAddress;
                            // Only pre-fill if fields are empty (user data takes priority)
                            if (!address1 && addr.address1) setAddress1(addr.address1);
                            if (!address2 && addr.address2) setAddress2(addr.address2);
                            if (!address3 && addr.address3) setAddress3(addr.address3);
                            if (!postCode && addr.postCode) setPostCode(addr.postCode);
                            // country is fixed to UK, ignore stored country
                            if (!city && addr.city) setCity(addr.city);
                        }
                        
                        // Prefill user info from order if user data is missing
                        // This ensures checkout works even if user profile is incomplete
                        if (latestOrder?.user) {
                            const orderUser = latestOrder.user;
                            if (!name && orderUser.name) setName(orderUser.name);
                            if (!email && orderUser.email) setEmail(orderUser.email);
                            if (!phoneNumber && orderUser.phoneNumber) setPhoneNumber(orderUser.phoneNumber);
                            if (!ukaraNumber && orderUser.ukaraNumber) setUkaraNumber(orderUser.ukaraNumber);
                        }
                    }
                })
                .catch((err) => {
                    // Silently fail - user might not have orders yet
                    console.log("No previous orders found");
                });
        }
    }, [user?._id, name, email, phoneNumber, ukaraNumber, address1, address2, address3, postCode, city]);

    // Update fields when user data changes - sync with profile updates
    const prevUserIdRef = React.useRef(null);
    useEffect(() => {
        if (user?._id && user._id !== prevUserIdRef.current) {
            prevUserIdRef.current = user._id;
            // Always update from user data when user changes
            if (user.name) setName(user.name);
            if (user.email) setEmail(user.email);
            if (user.phoneNumber) setPhoneNumber(user.phoneNumber);
            if (user.ukaraNumber) setUkaraNumber(user.ukaraNumber);
        } else if (user?._id) {
            // User ID is same but data might have been updated (e.g., from profile page)
            // Update fields if they're different from current user data
            if (user.name && user.name !== name) setName(user.name);
            if (user.email && user.email !== email) setEmail(user.email);
            if (user.phoneNumber && user.phoneNumber !== phoneNumber) setPhoneNumber(user.phoneNumber);
            if (user.ukaraNumber && user.ukaraNumber !== ukaraNumber) setUkaraNumber(user.ukaraNumber);
            
            // Sync address fields if user has updated saved addresses
            if (user.addresses && user.addresses.length > 0) {
                const savedAddr = user.addresses.find(addr => addr.addressType === "Default") || user.addresses[0];
                if (savedAddr) {
                    // Only update if fields are empty or if user explicitly changed saved address
                    if (savedAddr.address1 && (!address1 || savedAddr.address1 !== address1)) setAddress1(savedAddr.address1);
                    if (savedAddr.address2 && (!address2 || savedAddr.address2 !== address2)) setAddress2(savedAddr.address2);
                    if (savedAddr.address3 && (!address3 || savedAddr.address3 !== address3)) setAddress3(savedAddr.address3);
                    if (savedAddr.postCode && (!postCode || savedAddr.postCode !== postCode)) setPostCode(savedAddr.postCode);
                    if (savedAddr.city && (!city || savedAddr.city !== city)) setCity(savedAddr.city);
                }
            }
            // Also check shop address/post code updates
            if (user.shopAddress && !address1) setAddress1(user.shopAddress);
            if (user.shopPostCode && !postCode) setPostCode(user.shopPostCode);
        }
    }, [user?._id, user?.name, user?.email, user?.phoneNumber, user?.ukaraNumber, user?.addresses, user?.shopAddress, user?.shopPostCode, address1, address2, address3, postCode, city]);

    // Check if cart is empty
    useEffect(() => {
        if (!cart || cart.length === 0) {
            toast.error("Your cart is empty. Please add items before checkout.");
            navigate("/");
        }
    }, [cart, navigate]);

    useEffect(() => {
        const calculateBundleTotals = async () => {
            if (!cart || cart.length === 0) {
                setBundleDiscount(0);
                setBundlePostageDiscount(0);
                return;
            }

            const uniqueShopIds = Array.from(
                new Set(cart.map((item) => item.shopId).filter(Boolean))
            );

            if (uniqueShopIds.length === 0) {
                setBundleDiscount(0);
                setBundlePostageDiscount(0);
                return;
            }

            try {
                const results = await Promise.all(
                    uniqueShopIds.map((id) =>
                        axios
                            .get(`${server}/shop/get-shop-info/${id}`)
                            .then((res) => ({
                                id,
                                bundleRules: res.data?.shop?.bundleRules || [],
                            }))
                            .catch(() => ({ id, bundleRules: [] }))
                    )
                );

                const shopBundleRulesById = {};
                results.forEach(({ id, bundleRules }) => {
                    shopBundleRulesById[String(id)] = bundleRules;
                });

                const totals = computeCartBundleTotals(
                    cartWithPostage,
                    shopBundleRulesById,
                    bundlePostageConfig
                );
                setBundleDiscount(totals.bundleProductDiscount);
                setBundlePostageDiscount(totals.bundlePostageDiscount);
            } catch {
                setBundleDiscount(0);
                setBundlePostageDiscount(0);
            }
        };

        calculateBundleTotals();
    }, [cart, cartWithPostage, bundlePostageConfig]);

    useEffect(() => {
        const loadPaymentSettings = async () => {
            try {
                const { data } = await axios.get(`${server}/options/public-payment-settings`);
                const bp = data?.settings?.buyerProtection || {};
                setBuyerProtectionConfig({
                    enabled: bp.enabled !== false,
                    fixedFee: Number(bp.fixedFee ?? 0.7),
                    percentage: Number(bp.percentage ?? 2),
                });
                const bpd = data?.settings?.bundlePostageDiscount || {};
                setBundlePostageConfig({
                    enabled: bpd.enabled !== false,
                    percentage: Number(bpd.percentage ?? 30),
                });
            } catch {
                setBuyerProtectionConfig({
                    enabled: true,
                    fixedFee: 0.7,
                    percentage: 2,
                });
                setBundlePostageConfig(defaultBundlePostageConfig);
            }
        };

        loadPaymentSettings();
    }, []);

    const hasAirsoftGuns = useMemo(() => cartRequiresUkara(cart), [cart]);

    const paymentSubmit = async () => {
        // Convert to strings to ensure trim() works
        const nameStr = String(name || "").trim();
        const emailStr = String(email || "").trim();
        const phoneStr = String(phoneNumber || "").trim();
        const ukaraStr = String(ukaraNumber || "").trim();
        
        if (!nameStr || nameStr.length === 0) {
            toast.error("Please enter your full name");
            return;
        }
        if (!emailStr || emailStr.length === 0) {
            toast.error("Please enter your email address");
            return;
        }
        if (!phoneStr || phoneStr.length === 0) {
            toast.error("Please enter your phone number");
            return;
        }
        // Validate required delivery address fields (Address line 1, Post Code, City)
        if (!String(address1 || "").trim() || !String(postCode || "").trim() || !String(city || "").trim()) {
            toast.error("Please complete your delivery address (address line 1, county and postcode).");
            return;
        }
        if (hasAirsoftGuns && (!ukaraStr || ukaraStr.length === 0)) {
            toast.error("Please provide your UKARA number for replica firearms or ammunition items.");
            return;
        }
        if (!cart || cart.length === 0) {
            toast.error("Your cart is empty. Please add items before checkout.");
            navigate("/");
            return;
        }

        const stockCheck = await assertCartLinesInStock(cartWithPostage);
        if (!stockCheck.ok) {
            toast.error(stockCheck.message || "One or more items are no longer available.");
            return;
        }
        
        // All validations passed
        const shippingAddress = {
            address1,
            address2,
            address3,
            postCode,
            // country is implicitly UK
            country: "UK",
            city,
        };

        const normalizedCart = (cartWithPostage || []).map((item) => {
            const fallbackShopId =
                item?.shopId ||
                item?.shop?._id ||
                item?.shop?.id ||
                item?.shop?.shopId ||
                item?.shop?._doc?._id ||
                item?.sellerId ||
                item?.seller?._id ||
                item?.seller?.id ||
                item?.seller?.shopId ||
                (typeof item?.shop === "string" ? item.shop : "");
            return {
                ...item,
                shopId: fallbackShopId ? String(fallbackShopId) : undefined,
                productId: item?.productId || item?._id || item?.id || undefined,
            };
        });

        const orderData = {
            cart: normalizedCart,
            totalPrice,
            subTotalPrice,
            shipping: 0,
            discountPrice: effectiveCodeDiscount + effectiveVeteranDiscount,
            discountCode: appliedCode || undefined,
            postageFees,
            bundleDiscount: effectiveBundleDiscount,
            bundlePostageDiscount: effectiveBundlePostageDiscount,
            buyerProtectionFee,
            freeShipping: effectiveShippingDiscount > 0,
            shippingAddress,
            user: {
                ...user,
                name: nameStr,
                email: emailStr,
                phoneNumber: phoneStr,
                ukaraNumber: hasAirsoftGuns ? ukaraStr : "",
            },
            ukaraNumber: hasAirsoftGuns ? ukaraStr : "",
        };

        // Auto-save checkout address into profile saved addresses (if it's new)
        if (user && user._id) {
            try {
                const existingAddresses = Array.isArray(user.addresses) ? user.addresses : [];
                const trimmedAddress1 = String(address1 || "").trim();
                const trimmedAddress2 = String(address2 || "").trim();
                const trimmedAddress3 = String(address3 || "").trim();
                const trimmedPostCode = String(postCode || "").trim();

                const alreadyExists = existingAddresses.some((addr) =>
                    String(addr.address1 || "").trim() === trimmedAddress1 &&
                    String(addr.address2 || "").trim() === trimmedAddress2 &&
                    String(addr.address3 || "").trim() === trimmedAddress3 &&
                    String(addr.postCode || "").trim() === trimmedPostCode &&
                    String(addr.city || "") === String(city || "")
                );

                if (!alreadyExists) {
                    const existingTypes = new Set(
                        existingAddresses
                            .map((addr) => addr.addressType)
                            .filter(Boolean)
                    );

                    let addressType = "Default";
                    if (existingTypes.has(addressType)) addressType = "Home";
                    if (existingTypes.has(addressType)) addressType = "Office";
                    let counter = 1;
                    while (existingTypes.has(addressType)) {
                        addressType = `Address ${counter++}`;
                    }

                    dispatch(
                        updatUserAddress(
                            country,
                            city,
                            trimmedAddress1,
                            trimmedAddress2,
                            trimmedPostCode,
                            addressType,
                            trimmedAddress3
                        )
                    );
                }
            } catch (e) {
                console.error("Failed to auto-save checkout address to profile", e);
            }
        }

        const invalidItems = normalizedCart.filter((item) => !item?.shopId);
        if (invalidItems.length > 0) {
            const invalidNames = invalidItems
                .map((item) => item?.name || "Unnamed item")
                .slice(0, 3)
                .join(", ");
            toast.error(
                `Some basket items are missing seller details (${invalidNames}). Please remove and re-add them before checkout.`
            );
            return;
        }

        localStorage.setItem("latestOrder", JSON.stringify(orderData));
        navigate("/payment");
    };

    const subTotalPrice = sumCartSubtotal(cartWithPostage);

    // Calculate total postage fees from all cart items
    const postageFees = sumPostageFees(cartWithPostage);

    const buyerProtectionFee = computeBuyerProtectionFee(subTotalPrice, buyerProtectionConfig);

    // Product subtotal only (no postage) – used for discount min purchase and discount amount
    const productSubtotal = subTotalPrice - bundleDiscount;
    const bundleStackable = siteOptions?.discountStacking?.bundleStackable !== false;
    const shippingDiscount = codeFreeShipping ? postageFees : 0;

    // Veteran discount (verified users only; setting controlled in admin)
    const veteranDiscountSetting = siteOptions?.veteranDiscount;
    const isVeteranVerified = user?.veteranVerificationStatus === "verified";
    let veteranDiscountAmount = 0;
    if (isVeteranVerified && veteranDiscountSetting?.enabled && productSubtotal > 0) {
        if (veteranDiscountSetting.type === "percentage") {
            const pct = Math.min(100, Math.max(0, Number(veteranDiscountSetting.value) || 0));
            veteranDiscountAmount = Number(((productSubtotal * pct) / 100).toFixed(2));
        } else {
            veteranDiscountAmount = Math.min(productSubtotal, Number(veteranDiscountSetting.value) || 0);
            veteranDiscountAmount = Number(veteranDiscountAmount.toFixed(2));
        }
    }

    // Stacking: when allowStacking is true, apply all stackable discounts; when false, apply only the best one
    const allowStacking = siteOptions?.discountStacking?.allowStacking !== false;
    const veteranStackable = veteranDiscountSetting?.stackable !== false;
    let effectiveCodeDiscount = 0;
    let effectiveVeteranDiscount = 0;
    let effectiveBundleDiscount = 0;
    let effectiveShippingDiscount = shippingDiscount;

    if (allowStacking) {
        effectiveCodeDiscount = codeStackable ? codeDiscount : 0;
        effectiveVeteranDiscount = veteranStackable ? veteranDiscountAmount : 0;
        effectiveBundleDiscount = bundleStackable ? bundleDiscount : 0;
        // Free shipping is independent of stackable — valid free-shipping codes always waive postage.
        effectiveShippingDiscount = codeFreeShipping ? postageFees : 0;
    } else {
        const best = Math.max(codeDiscount, veteranDiscountAmount, bundleDiscount);
        if (best === codeDiscount && codeDiscount > 0) {
            effectiveCodeDiscount = codeDiscount;
        } else if (best === veteranDiscountAmount && veteranDiscountAmount > 0) {
            effectiveVeteranDiscount = veteranDiscountAmount;
        } else if (best === bundleDiscount && bundleDiscount > 0) {
            effectiveBundleDiscount = bundleDiscount;
        }
        if (codeFreeShipping) {
            effectiveShippingDiscount = postageFees;
        }
    }

    const effectiveBundlePostageDiscount =
        bundleStackable && effectiveShippingDiscount <= 0 ? bundlePostageDiscount : 0;

    const totalBeforeCode =
        subTotalPrice +
        postageFees +
        buyerProtectionFee -
        effectiveBundleDiscount -
        effectiveBundlePostageDiscount;

    // totalBeforeCode already includes bundle discounts; only subtract code/veteran/shipping here
    // (adding effectiveBundleDiscount again would double-count the bundle — e.g. £137.20 too low).
    const totalPrice = (
        totalBeforeCode -
        effectiveCodeDiscount -
        effectiveVeteranDiscount -
        effectiveShippingDiscount
    ).toFixed(2);

    const applyDiscountCode = () => {
        const code = String(discountCode || "").trim();
        if (!code) {
            setCodeDiscountError("Enter a discount code");
            return;
        }
        setCodeDiscountError("");
        setCodeDiscountLoading(true);
        // Min purchase and discount apply to product price only; postage is separate (only counted for free shipping codes)
        axios
            .post(`${server}/discount/validate`, { code, amount: productSubtotal }, { withCredentials: true })
            .then((res) => {
                const data = res.data || {};
                if (data.valid && (data.discountAmount > 0 || data.freeShipping)) {
                    setCodeDiscount(Number(data.discountAmount));
                    setCodeFreeShipping(!!data.freeShipping);
                    setCodeStackable(!!data.stackable);
                    setAppliedCode(code.toUpperCase());
                    setCodeDiscountError("");
                } else {
                    setCodeDiscount(0);
                    setCodeFreeShipping(false);
                    setCodeStackable(false);
                    setAppliedCode("");
                    setCodeDiscountError(data.message || "Code is not valid");
                }
            })
            .catch(() => {
                setCodeDiscount(0);
                setCodeFreeShipping(false);
                setCodeStackable(false);
                setAppliedCode("");
                setCodeDiscountError("Could not validate code");
            })
            .finally(() => setCodeDiscountLoading(false));
    };

    const removeDiscountCode = () => {
        setDiscountCode("");
        setAppliedCode("");
        setCodeDiscount(0);
        setCodeFreeShipping(false);
        setCodeStackable(false);
        setCodeDiscountError("");
    };

    const handleSelectSavedAddress = (item) => {
        setAddress1(item.address1 || "");
        setAddress2(item.address2 || "");
        setAddress3(item.address3 || "");
        setPostCode(item.postCode || "");
        setCity(item.city || "");
        // Don't overwrite ukaraNumber if user has already entered one
        if (!ukaraNumber) {
            setUkaraNumber(user?.ukaraNumber || "");
        }
        setUserInfo(false); // Close the dropdown after selection
    };

    return (
        <div className="w-full flex flex-col items-center py-8">
            <div className="w-[95%] 1000px:w-[70%] flex flex-col gap-6 800px:flex-row 800px:gap-8">
                <div className="w-full 800px:w-[65%]">
                    <ShippingInfo
                        user={user}
                        name={name}
                        setName={setName}
                        email={email}
                        setEmail={setEmail}
                        phoneNumber={phoneNumber}
                        setPhoneNumber={setPhoneNumber}
                        city={city}
                        setCity={setCity}
                        userInfo={userInfo}
                        setUserInfo={setUserInfo}
                        address1={address1}
                        setAddress1={setAddress1}
                        address2={address2}
                        setAddress2={setAddress2}
                        address3={address3}
                        setAddress3={setAddress3}
                        postCode={postCode}
                        setPostCode={setPostCode}
                        ukaraNumber={ukaraNumber}
                        setUkaraNumber={setUkaraNumber}
                        handleSelectSavedAddress={handleSelectSavedAddress}
                        hasAirsoftGuns={hasAirsoftGuns}
                    />
                </div>
                <div className="w-full 800px:w-[35%] 800px:mt-0">
                    <CartData
                        cart={cart}
                        totalPrice={totalPrice}
                        buyerProtectionFee={buyerProtectionFee}
                        bundleDiscount={effectiveBundleDiscount}
                        bundlePostageDiscount={effectiveBundlePostageDiscount}
                        bundlePostagePercent={bundlePostageConfig.percentage}
                        codeDiscount={effectiveCodeDiscount}
                        veteranDiscountAmount={effectiveVeteranDiscount}
                        codeFreeShipping={effectiveShippingDiscount > 0}
                        appliedCode={appliedCode}
                        discountCode={discountCode}
                        setDiscountCode={setDiscountCode}
                        applyDiscountCode={applyDiscountCode}
                        removeDiscountCode={removeDiscountCode}
                        codeDiscountError={codeDiscountError}
                        codeDiscountLoading={codeDiscountLoading}
                        subTotalPrice={subTotalPrice}
                        postageFees={postageFees}
                        dispatch={dispatch}
                    />
                </div>
            </div>
            <div className="w-[95%] 1000px:w-[70%] flex flex-col gap-3 mt-6 800px:flex-row 800px:gap-4 800px:justify-end">
                <button
                    onClick={() => navigate(-1)}
                    className="w-full 800px:w-[200px] bg-[#38513B] h-[50px] my-3 flex items-center justify-center rounded-xl cursor-pointer font-[600] text-white px-5"
                >
                    Back
                </button>
                <div
                    className={`${styles.button} w-full checkout-button`}
                    onClick={paymentSubmit}
                >
                    <h5 className="text-white">
                        Continue to Payment
                    </h5>
                </div>
            </div>
        </div>
    );
};

const ShippingInfo = ({
    user,
    name,
    setName,
    email,
    setEmail,
    phoneNumber,
    setPhoneNumber,
    city,
    setCity,
    userInfo,
    setUserInfo,
    address1,
    setAddress1,
    address2,
    setAddress2,
    address3,
    setAddress3,
    postCode,
    setPostCode,
    ukaraNumber,
    setUkaraNumber,
    handleSelectSavedAddress,
    hasAirsoftGuns,
}) => {
    return (
        <div className="w-full bg-white rounded-md p-5 pb-8">
            <h5 className="text-[18px] font-[500]">Shipping Address</h5>
            <br />
            <form>
                <div className="w-full flex flex-col gap-3 pb-3 800px:flex-row">
                    <div className="w-full 800px:w-[50%]">
                        <label className="block pb-2">Full Name</label>
                        <input
                            type="text"
                            value={name || ""}
                            onChange={(e) => setName(e.target.value)}
                            required
                            className={`${styles.input} w-full`}
                            placeholder="Enter your full name"
                        />
                    </div>
                    <div className="w-full 800px:w-[50%]">
                        <label className="block pb-2">Email Address</label>
                        <input
                            type="email"
                            value={email || ""}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className={`${styles.input}`}
                            placeholder="Enter your email"
                        />
                    </div>
                </div>

                <div className="w-full flex flex-col gap-3 pb-3 800px:flex-row">
                    <div className="w-full 800px:w-[50%]">
                        <label className="block pb-2">Phone Number</label>
                        <input
                            type="tel"
                            required
                            value={phoneNumber || ""}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            className={`${styles.input} w-full`}
                            placeholder="Enter your phone number"
                        />
                    </div>
                    <div className="w-full 800px:w-[50%]">
                        <label className="block pb-2">Post Code</label>
                        <input
                            type="text"
                            value={postCode || ""}
                            onChange={(e) => setPostCode(e.target.value)}
                            required
                            className={`${styles.input}`}
                        />
                    </div>
                </div>

                {hasAirsoftGuns && (
                    <div className="w-full pb-3">
                        <label className="block pb-2">
                            UKARA Number <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={ukaraNumber}
                            onChange={(e) =>
                                setUkaraNumber(e.target.value.replace(/\s+/g, "").toUpperCase())
                            }
                            required
                            className={`${styles.input}`}
                            placeholder="e.g. ABC123456"
                        />
                        <span className="text-xs text-[#555]">
                            This number will be shared with the seller for verification before shipment.
                        </span>
                    </div>
                )}

                <div className="w-full flex flex-col gap-3 pb-3 800px:flex-row">
                    <div className="w-full 800px:w-[50%]">
                        <label className="block pb-2">County</label>
                        <select
                            className="w-full border h-[40px] rounded-[5px]"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            required
                        >
                            <option className="block pb-2" value="">
                                Choose your county
                            </option>
                            {State &&
                                State.getStatesOfCountry("GB").map((item) => (
                                    <option key={item.isoCode} value={item.isoCode}>
                                        {item.name}
                                    </option>
                                ))}
                        </select>
                    </div>
                </div>

                <div className="w-full flex flex-col gap-3 pb-3 800px:flex-row">
                    <div className="w-full 800px:w-[50%]">
                        <label className="block pb-2">Address line 1</label>
                        <input
                            type="address"
                            required
                            value={address1}
                            onChange={(e) => setAddress1(e.target.value)}
                            className={`${styles.input} w-full`}
                        />
                    </div>
                    <div className="w-full 800px:w-[50%]">
                        <label className="block pb-2">Address line 2 (optional)</label>
                        <input
                            type="address"
                            value={address2}
                            onChange={(e) => setAddress2(e.target.value)}
                            className={`${styles.input}`}
                        />
                    </div>
                </div>
                <div className="w-full flex flex-col gap-3 pb-3 800px:flex-row">
                    <div className="w-full 800px:w-[50%]">
                        <label className="block pb-2">Address line 3 (optional)</label>
                        <input
                            type="address"
                            value={address3}
                            onChange={(e) => setAddress3(e.target.value)}
                            className={`${styles.input}`}
                        />
                    </div>
                </div>

                <div></div>
            </form>
            {user && user.addresses && user.addresses.length > 0 && (
                <>
                    <h5 className="text-[18px] inline-block text-[#38513b]">
                        Select a saved address ({user.addresses.length})
                    </h5>
                    <div className="mt-3 space-y-2 border-t pt-3">
                        {user.addresses.map((item, index) => (
                            <div
                                key={index}
                                className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition"
                                onClick={() => handleSelectSavedAddress(item)}
                            >
                                <input
                                    type="radio"
                                    name="savedAddress"
                                    className="cursor-pointer"
                                    checked={
                                        address1 === item.address1 &&
                                        address2 === item.address2 &&
                                        address3 === (item.address3 || "") &&
                                        postCode === item.postCode &&
                                        city === item.city
                                    }
                                    onChange={() => handleSelectSavedAddress(item)}
                                />
                                <div className="flex-1">
                                    <h3 className="font-semibold text-slate-900">
                                        {item.addressType}
                                    </h3>
                                    <p className="text-sm text-slate-600">
                                        {item.address1}
                                        {item.address2 ? `, ${item.address2}` : ""}
                                        {item.address3 ? `, ${item.address3}` : ""}
                                        {item.postCode ? `, ${item.postCode}` : ""}
                                    </p>
                                    <p className="text-xs text-slate-500">
                                        {item.city}, {item.country}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

const CartData = ({
    cart,
    totalPrice,
    buyerProtectionFee,
    bundleDiscount = 0,
    bundlePostageDiscount = 0,
    bundlePostagePercent = 30,
    codeDiscount = 0,
    veteranDiscountAmount = 0,
    codeFreeShipping = false,
    appliedCode = "",
    discountCode = "",
    setDiscountCode,
    applyDiscountCode,
    removeDiscountCode,
    codeDiscountError = "",
    codeDiscountLoading = false,
    subTotalPrice,
    postageFees = 0,
    dispatch,
}) => {
    const [bundleWarning, setBundleWarning] = useState({
        open: false,
        item: null,
        text: "",
    });

    const quantityChangeHandler = (data) => {
        dispatch(addTocart(data));
    };

    const executeRemoveFromCart = (data) => {
        if (!data) return;
        if (data?.isBundleItemFromOffer || data?.bundleOfferId) {
            dispatch(removeBundleDealFromCart(data));
        }
        dispatch(removeFromCart(data));
    };

    const removeFromCartHandler = (data) => {
        const hasAnotherItemFromSameShop = cart.some(
            (item) => item?._id !== data?._id && item?.shopId && item?.shopId === data?.shopId
        );
        const shouldWarn =
            data?.isBundleItemFromOffer || data?.bundleOfferId || hasAnotherItemFromSameShop;

        if (!shouldWarn) {
            executeRemoveFromCart(data);
            return;
        }

        const warningText = data?.isBundleItemFromOffer || data?.bundleOfferId
            ? "This negotiated bundle offer will be cancelled and the remaining items will return to their original prices."
            : "If this item is part of a bundle discount, pricing will be recalculated when the bundle is broken.";

        setBundleWarning({
            open: true,
            item: data,
            text: warningText,
        });
    };

    const confirmBundleRemoval = () => {
        executeRemoveFromCart(bundleWarning.item);
        setBundleWarning({ open: false, item: null, text: "" });
    };

    return (
        <div className="w-full bg-[#fff] rounded-md p-5 pb-8">
            <BundleRemovalWarningModal
                open={bundleWarning.open}
                itemName={bundleWarning?.item?.name || "this item"}
                warningText={bundleWarning.text}
                onClose={() => setBundleWarning({ open: false, item: null, text: "" })}
                onConfirm={confirmBundleRemoval}
            />
            <h3 className="text-[20px] font-[600] mb-4">Order Summary</h3>

            {/* Discount code */}
            <div className="mb-4 pb-4 border-b border-slate-200">
                {!appliedCode ? (
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <input
                            type="text"
                            value={discountCode}
                            onChange={(e) => setDiscountCode(e.target.value)}
                            placeholder="Discount code"
                            className="w-full min-w-0 flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-[#38513b] focus:border-[#38513b]"
                        />
                        <button
                            type="button"
                            onClick={applyDiscountCode}
                            disabled={codeDiscountLoading}
                            className="w-full sm:w-auto px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200 disabled:opacity-60"
                        >
                            {codeDiscountLoading ? "Applying..." : "Apply"}
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center justify-between gap-2 py-1">
                        <span className="text-sm text-green-700 font-medium">
                            Discount <span className="font-mono">{appliedCode}</span> applied
                        </span>
                        <button
                            type="button"
                            onClick={removeDiscountCode}
                            className="text-sm text-red-600 hover:underline"
                        >
                            Remove
                        </button>
                    </div>
                )}
                {codeDiscountError && (
                    <p className="text-xs text-red-600 mt-1">{codeDiscountError}</p>
                )}
            </div>
            
            {/* Products List */}
            <div className="max-h-[400px] overflow-y-auto mb-4 border-b pb-4">
                {cart && cart.length > 0 ? (
                    <div className="space-y-3">
                        {cart.map((item, index) => (
                            <div key={index} className="flex items-start gap-3 pb-3 border-b last:border-0">
                                <Link
                                    to={`/product/${item._id}`}
                                    className="shrink-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#38513b]"
                                    aria-label={`View ${normalizeProductDisplayName(item.name)}`}
                                >
                                    <img
                                        src={
                                            item.images && item.images.length > 0
                                                ? item.images[0]?.url
                                                    ? `${backend_url}${item.images[0].url}`
                                                    : `${backend_url}${item.images[0]}`
                                                : item.image
                                                    ? `${backend_url}${item.image}`
                                                    : "/placeholder-image.png"
                                        }
                                        alt={item.name || "Product"}
                                        className="w-16 h-16 object-contain rounded-md"
                                        onError={(e) => {
                                            e.target.src = "/placeholder-image.png";
                                        }}
                                    />
                                </Link>
                                <div className="flex-1 min-w-0">
                                    <Link
                                        to={`/product/${item._id}`}
                                        className="block text-[14px] font-[500] text-[#333] line-clamp-2 mb-1 hover:text-[#38513b] hover:underline"
                                    >
                                        {normalizeProductDisplayName(item.name)}
                                    </Link>
                                    <p className="text-[14px] font-[600] text-[#38513B] mb-2">
                                        {formatCurrency(item.discountPrice || 0)}
                                    </p>
                                    
                                    {/* Quantity Controls */}
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (item.qty > 1) {
                                                    quantityChangeHandler({ ...item, qty: item.qty - 1 });
                                                }
                                            }}
                                            disabled={item.qty <= 1}
                                            className="w-7 h-7 flex items-center justify-center border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <HiOutlineMinus size={14} />
                                        </button>
                                        <span className="text-sm font-medium w-8 text-center">
                                            {item.qty}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (item.stock > item.qty) {
                                                    quantityChangeHandler({ ...item, qty: item.qty + 1 });
                                                } else {
                                                    toast.error("Stock limit reached");
                                                }
                                            }}
                                            disabled={item.stock <= item.qty}
                                            className="w-7 h-7 flex items-center justify-center border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <HiPlus size={14} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => removeFromCartHandler(item)}
                                            className="ml-auto text-red-500 hover:text-red-700 p-1"
                                            title="Remove item"
                                        >
                                            <RxCross1 size={18} />
                                        </button>
                                    </div>
                                    <p className="text-[12px] text-[#666] mt-1">
                                        Subtotal: {formatCurrency((item.qty || 0) * (item.discountPrice || 0))}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-[14px] text-[#666] text-center py-4">No items in cart</p>
                )}
            </div>

            {/* Price Breakdown */}
            <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                    <h3 className="text-[16px] font-[400] text-[#000000a4]">Subtotal:</h3>
                    <h5 className="text-[18px] font-[600]">{formatCurrency(subTotalPrice)}</h5>
                </div>
                {postageFees > 0 && (
                    <div className="flex justify-between">
                        <h3 className="text-[16px] font-[400] text-[#000000a4]">Postage fees:</h3>
                        <h5 className="text-[18px] font-[600]">{formatCurrency(postageFees)}</h5>
                    </div>
                )}
                {bundlePostageDiscount > 0 && (
                    <div className="flex justify-between">
                        <h3 className="text-[16px] font-[400] text-[#000000a4]">
                            {bundlePostageDiscountLabel(bundlePostagePercent)}
                        </h3>
                        <h5 className="text-[18px] font-[600] text-green-600">
                            - {formatCurrency(bundlePostageDiscount)}
                        </h5>
                    </div>
                )}
                {buyerProtectionFee > 0 && (
                    <div className="flex justify-between">
                        <h3 className="text-[16px] font-[400] text-[#000000a4]">Buyer protection fee:</h3>
                        <h5 className="text-[18px] font-[600]">{formatCurrency(buyerProtectionFee)}</h5>
                    </div>
                )}
                {bundleDiscount > 0 && (
                    <div className="flex justify-between">
                        <h3 className="text-[16px] font-[400] text-[#000000a4]">Bundle discount:</h3>
                        <h5 className="text-[18px] font-[600] text-green-600">
                            - {formatCurrency(bundleDiscount)}
                        </h5>
                    </div>
                )}
                {codeDiscount > 0 && (
                    <div className="flex justify-between">
                        <h3 className="text-[16px] font-[400] text-[#000000a4]">Discount ({appliedCode}):</h3>
                        <h5 className="text-[18px] font-[600] text-green-600">
                            - {formatCurrency(codeDiscount)}
                        </h5>
                    </div>
                )}
                {veteranDiscountAmount > 0 && (
                    <div className="flex justify-between">
                        <h3 className="text-[16px] font-[400] text-[#000000a4]">Veteran discount:</h3>
                        <h5 className="text-[18px] font-[600] text-green-600">
                            - {formatCurrency(veteranDiscountAmount)}
                        </h5>
                    </div>
                )}
                {codeFreeShipping && postageFees > 0 && (
                    <div className="flex justify-between">
                        <h3 className="text-[16px] font-[400] text-[#000000a4]">Free shipping:</h3>
                        <h5 className="text-[18px] font-[600] text-green-600">
                            - {formatCurrency(postageFees)}
                        </h5>
                    </div>
                )}
                <div className="flex justify-between pt-2 border-t">
                    <h3 className="text-[18px] font-[600]">Total:</h3>
                    <h5 className="text-[20px] font-[700] text-[#38513B]">{formatCurrency(totalPrice)}</h5>
                </div>
            </div>
        </div>
    );
};

export default Checkout;
